import { BadgePercent, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { confirmModal, Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { apiErrorMessage } from '@/i18n'
import { type Incentive, useDeleteIncentive, useIncentives, usePayrollParams, useSaveIncentive } from '../../api'

type Draft = Omit<Incentive, 'assigned'> & { assigned: number; dirty?: boolean; key: string }

/**
 * SGK teşvik kataloğu: her teşvikin işveren SGK payından düşülen yüzde puanı. Varsayılan indirim (5510) bordro
 * parametrelerindedir; burada tanımlanan teşvikler personele dönem dönem atanır.
 */
export function IncentiveCatalogModal({ open, canManage, onClose }: { open: boolean; canManage: boolean; onClose: () => void }) {
  return open ? <Catalog canManage={canManage} onClose={onClose} /> : null
}

function Catalog({ canManage, onClose }: { canManage: boolean; onClose: () => void }) {
  const { t } = useTranslation()
  const list = useIncentives()
  const params = usePayrollParams()
  const save = useSaveIncentive()
  const remove = useDeleteIncentive()
  const [drafts, setDrafts] = useState<Draft[] | null>(null)
  const rows: Draft[] = drafts ?? (list.data ?? []).map((i) => ({ ...i, key: i.id }))
  const latest = [...(params.data ?? [])].sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0]

  const patch = (key: string, p: Partial<Draft>) => setDrafts(rows.map((r) => (r.key === key ? { ...r, ...p, dirty: true } : r)))
  const add = () => setDrafts([...rows, { id: '', key: crypto.randomUUID(), code: '', name: '', rate: '', active: true, assigned: 0, dirty: true }])

  const onSave = async () => {
    const changed = rows.filter((r) => r.dirty)
    if (changed.some((r) => !r.code.trim() || !r.name.trim())) {
      toast.error(t('hr.personel.tesvik.required'))
      return
    }
    try {
      for (const r of changed) await save.mutateAsync(r)
      toast.success(t('hr.personel.tesvik.saved'))
      setDrafts(null)
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  }
  const onDelete = async (r: Draft) => {
    if (!r.id) {
      setDrafts(rows.filter((x) => x.key !== r.key))
      return
    }
    if (!(await confirmModal({ title: t('hr.personel.tesvik.deleteTitle'), description: `${r.code} · ${r.name}`, confirmLabel: t('common.delete'), destructive: true }))) return
    remove.mutate(r.id, { onSuccess: () => setDrafts(null), onError: (e) => toast.error(apiErrorMessage(e)) })
  }

  return (
    <Modal
      open
      size="xl"
      onOpenChange={(v) => !v && onClose()}
      icon={<BadgePercent />}
      title={t('hr.personel.tesvik.catalogTitle')}
      description={t('hr.personel.tesvik.catalogHint', {
        default: latest?.employerDiscountRate || '—',
        employer: latest?.employerSgkRate || '—',
      })}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.close')}
          </Button>
          {canManage ? (
            <Button type="button" disabled={!rows.some((r) => r.dirty) || save.isPending} onClick={() => void onSave()}>
              {t('common.save')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="space-y-2">
        <div className="hidden items-center gap-2 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex">
          <span className="w-24">{t('hr.personel.tesvik.code')}</span>
          <span className="flex-1">{t('hr.personel.tesvik.name')}</span>
          <span className="w-28">{t('hr.personel.tesvik.rate')}</span>
          <span className="w-20">{t('hr.personel.tesvik.active')}</span>
          <span className="w-24">{t('hr.personel.tesvik.assigned')}</span>
          <span className="w-8" />
        </div>
        {rows.map((r) => (
          <div key={r.key} className="flex flex-wrap items-center gap-2 rounded-xl border bg-card p-2 sm:flex-nowrap">
            <Input className="w-24" value={r.code} maxLength={20} disabled={!canManage} aria-label={t('hr.personel.tesvik.code')} onChange={(e) => patch(r.key, { code: e.target.value })} />
            <Input className="min-w-0 flex-1 basis-40" value={r.name} maxLength={100} disabled={!canManage} aria-label={t('hr.personel.tesvik.name')} onChange={(e) => patch(r.key, { name: e.target.value })} />
            <NumberInput className="w-28" value={r.rate} onChange={(v) => patch(r.key, { rate: v })} maxDecimals={2} suffix="%" disabled={!canManage} aria-label={t('hr.personel.tesvik.rate')} />
            <label className="flex w-20 items-center gap-2 text-sm">
              <input type="checkbox" className="size-4 accent-primary" checked={r.active} disabled={!canManage} onChange={(e) => patch(r.key, { active: e.target.checked })} />
              {t('hr.personel.tesvik.activeShort')}
            </label>
            <span className="w-24 text-xs text-muted-foreground">
              {t('hr.personel.tesvik.assignedCount', { count: r.assigned })}
              {Number(r.rate || 0) === 0 ? (
                <Badge tone="warning" className="ml-1">
                  {t('hr.personel.tesvik.zero')}
                </Badge>
              ) : null}
            </span>
            {canManage ? (
              <Button type="button" variant="ghost" size="icon" className="ml-auto size-8 text-muted-foreground hover:text-destructive sm:ml-0" aria-label={t('common.delete')} onClick={() => void onDelete(r)}>
                <Trash2 />
              </Button>
            ) : null}
          </div>
        ))}
        {canManage ? (
          <Button type="button" variant="outline" size="sm" onClick={add}>
            <Plus />
            {t('hr.personel.tesvik.add')}
          </Button>
        ) : null}
      </div>
    </Modal>
  )
}
